// src/components/auth/Login.tsx
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { logUserLogin } from "@/utils/activityLogger";
import { motion } from "framer-motion";
import { Eye, EyeOff, Lock, Mail, Package, User, UserPlus } from "lucide-react";
import { useState, type ChangeEvent, type FormEvent } from "react";
import { useAuth } from "../../contexts/AuthContext";

interface FormData {
    email: string
    password: string
    confirmPassword: string
    firstName: string
    lastName: string
}

interface FormErrors {
    email?: string
    password?: string
    confirmPassword?: string
    firstName?: string
    lastName?: string
    submit?: string
}

export default function Login() {
    const [isLogin, setIsLogin] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState<FormData>({
        email: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { signIn, signUp, loading } = useAuth();

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        // Email validation
        if (!formData.email) {
            newErrors.email = "Email è obbligatoria";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Email non valida";
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = "Password è obbligatoria";
        } else if (formData.password.length < 6) {
            newErrors.password = "Password deve essere almeno 6 caratteri";
        }

        // Additional validation for registration
        if (!isLogin) {
            if (!formData.confirmPassword) {
                newErrors.confirmPassword = "Conferma password è obbligatoria";
            } else if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = "Le password non coincidono";
            }

            if (!formData.firstName.trim()) {
                newErrors.firstName = "Nome è obbligatorio";
            }

            if (!formData.lastName.trim()) {
                newErrors.lastName = "Cognome è obbligatorio";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);
        setErrors({});

        try {
            let result;

            if (isLogin) {
                result = await signIn(formData.email, formData.password);
            } else {
                // For registration, set default role as "registrato"
                result = await signUp(formData.email, formData.password, {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                });
            }

            if (result.error) {
                // Handle specific Supabase errors
                let errorMessage = result.error.message;

                if (result.error.message.includes('Invalid login credentials')) {
                    errorMessage = 'Email o password non corretti';
                } else if (result.error.message.includes('User already registered')) {
                    errorMessage = 'Utente già registrato con questa email';
                } else if (result.error.message.includes('Email not confirmed')) {
                    errorMessage = 'Conferma la tua email prima di accedere';
                }

                setErrors({ submit: errorMessage });
            } else if (isLogin && result.data?.user) {
                // Login successful - log activity
                await logUserLogin(formData.email);
            } else if (!isLogin && result.data?.user) {
                // Registration successful
                setErrors({
                    submit: 'Registrazione completata! Controlla la tua email per confermare l\'account. Il tuo account sarà attivato da un amministratore.'
                });
                // Switch to login mode
                setTimeout(() => {
                    setIsLogin(true);
                    setErrors({});
                }, 4000);
            }
        } catch (error) {
            console.error('Auth error:', error);
            setErrors({ submit: 'Si è verificato un errore. Riprova.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: "" }));
        }
    };

    const toggleMode = () => {
        setIsLogin(!isLogin);
        setFormData({
            email: "",
            password: "",
            confirmPassword: "",
            firstName: "",
            lastName: "",
        });
        setErrors({});
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                {/* Header */}
                <div className="text-center mb-8">
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2 }}
                        className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg"
                    >
                        <Package className="w-10 h-10 text-white" />
                    </motion.div>
                    <h1 className="text-3xl font-bold text-slate-800 mb-2">
                        FastMag
                    </h1>
                    <p className="text-slate-600">
                        {isLogin ? "Accedi al tuo account" : "Crea un nuovo account"}
                    </p>
                </div>

                {/* Form */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8"
                >
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Name fields for registration */}
                        {!isLogin && (
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="firstName" className="flex items-center space-x-2">
                                        <User className="w-4 h-4" />
                                        <span>Nome *</span>
                                    </Label>
                                    <Input
                                        id="firstName"
                                        type="text"
                                        value={formData.firstName}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange("firstName", e.target.value)}
                                        placeholder="Mario"
                                        className={errors.firstName ? "border-red-500" : ""}
                                    />
                                    {errors.firstName && (
                                        <p className="text-sm text-red-500">{errors.firstName}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="lastName" className="flex items-center space-x-2">
                                        <User className="w-4 h-4" />
                                        <span>Cognome *</span>
                                    </Label>
                                    <Input
                                        id="lastName"
                                        type="text"
                                        value={formData.lastName}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange("lastName", e.target.value)}
                                        placeholder="Rossi"
                                        className={errors.lastName ? "border-red-500" : ""}
                                    />
                                    {errors.lastName && (
                                        <p className="text-sm text-red-500">{errors.lastName}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Email */}
                        <div className="space-y-2">
                            <Label htmlFor="email" className="flex items-center space-x-2">
                                <Mail className="w-4 h-4" />
                                <span>Email *</span>
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange("email", e.target.value)}
                                placeholder="mario.rossi@email.com"
                                className={errors.email ? "border-red-500" : ""}
                            />
                            {errors.email && (
                                <p className="text-sm text-red-500">{errors.email}</p>
                            )}
                        </div>

                        {/* Password */}
                        <div className="space-y-2">
                            <Label htmlFor="password" className="flex items-center space-x-2">
                                <Lock className="w-4 h-4" />
                                <span>Password *</span>
                            </Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    value={formData.password}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange("password", e.target.value)}
                                    placeholder="••••••••"
                                    className={`pr-10 ${errors.password ? "border-red-500" : ""}`}
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                                >
                                    {showPassword ? (
                                        <EyeOff className="w-4 h-4 text-slate-400" />
                                    ) : (
                                        <Eye className="w-4 h-4 text-slate-400" />
                                    )}
                                </Button>
                            </div>
                            {errors.password && (
                                <p className="text-sm text-red-500">{errors.password}</p>
                            )}
                        </div>

                        {/* Confirm Password for registration */}
                        {!isLogin && (
                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword" className="flex items-center space-x-2">
                                    <Lock className="w-4 h-4" />
                                    <span>Conferma Password *</span>
                                </Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    value={formData.confirmPassword}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange("confirmPassword", e.target.value)}
                                    placeholder="••••••••"
                                    className={errors.confirmPassword ? "border-red-500" : ""}
                                />
                                {errors.confirmPassword && (
                                    <p className="text-sm text-red-500">{errors.confirmPassword}</p>
                                )}
                            </div>
                        )}

                        {/* Submit Error */}
                        {errors.submit && (
                            <div className={`p-3 rounded-lg text-sm ${errors.submit.includes('completata')
                                    ? 'bg-green-100 text-green-700 border border-green-200'
                                    : 'bg-red-100 text-red-700 border border-red-200'
                                }`}>
                                {errors.submit}
                            </div>
                        )}

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            disabled={isSubmitting || loading}
                            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-6 text-lg font-medium"
                        >
                            {isSubmitting || loading ? (
                                <div className="flex items-center space-x-2">
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>{isLogin ? "Accesso..." : "Registrazione..."}</span>
                                </div>
                            ) : (
                                <div className="flex items-center space-x-2">
                                    {isLogin ? <Lock className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                                    <span>{isLogin ? "Accedi" : "Registrati"}</span>
                                </div>
                            )}
                        </Button>
                    </form>

                    {/* Toggle Mode */}
                    <div className="mt-6 text-center">
                        <button
                            type="button"
                            onClick={toggleMode}
                            className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
                        >
                            {isLogin
                                ? "Non hai un account? Registrati"
                                : "Hai già un account? Accedi"
                            }
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
}
